import React from "react";
import { Responsive, WidthProvider, Layout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Board } from "../../../lib/api/boards";
import { useTranslation } from "react-i18next";
import { getChartComponent, ChartContainer, getChartDefinition } from "./charts";
import { generateResponsiveLayouts } from "./charts/chartStorage";


const ResponsiveGridLayout = WidthProvider(Responsive);

import { SizeTier } from "./charts/types";
import { BoardGridContextMenu } from "./BoardGridContextMenu";

export interface GridItem extends Layout {
  type: string;
  sizeTier: SizeTier;
  order: number;
  aspectRatio: string;
  _editBp?: string; // Tracks which breakpoint the current x,y,w,h are from
}


interface BoardGridProps {
  board?: Board;
  items: GridItem[];
  isEditMode?: boolean;
  isChartLoading?: boolean;
  isChartError?: boolean;
  onChartErrorClick?: () => void;
  onLayoutChange: (layout: Layout[]) => void;
  onDragStop?: (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => void;
  onResizeStop?: (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => void;
  onDrop: (layout: Layout[], item: Layout, e: Event) => void;
  onRemoveItem?: (itemId: string) => void;
  droppingItem?: { i: string; w: number; h: number };
  onDrag?: (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => void;
  onResize?: (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => void;
  onAddCharts?: (location?: { x: number, y: number }) => void;
  onToggleEditMode?: (enabled: boolean) => void;
}

export const BoardGrid: React.FC<BoardGridProps> = ({
  board,
  items,
  isEditMode = false,
  isChartLoading = false,
  isChartError = false,
  onChartErrorClick,
  onLayoutChange,
  onDragStop,
  onResizeStop,
  onDrop,
  onRemoveItem,
  droppingItem,
  onDrag,
  onResize,
  onAddCharts,
  onToggleEditMode,
}) => {
  const { t } = useTranslation('common');
  const [rowHeight, setRowHeight] = React.useState(100);
  const [currentCols, setCurrentCols] = React.useState(12);

  // Context Menu State
  const [contextMenu, setContextMenu] = React.useState<{
    isOpen: boolean;
    x: number;
    y: number;
    targetChartId?: string;
  }>({
    isOpen: false,
    x: 0,
    y: 0
  });

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Find if we clicked within a chart using event delegation
    const target = e.target as HTMLElement;
    const chartElement = target.closest('[data-chart-id]');
    const chartId = chartElement?.getAttribute('data-chart-id') || undefined;

    setContextMenu({
      isOpen: true,
      x: e.clientX,
      y: e.clientY,
      targetChartId: chartId
    });
  };

  const maxRow = items.reduce((max, item) => Math.max(max, item.y + item.h), 0);
  const totalRowsForBg = Math.max(15, maxRow + 10);

  // Generate responsive layouts for all breakpoints
  const layouts = React.useMemo(() => {
    return generateResponsiveLayouts(items);
  }, [items]);

  const handleWidthChange = (
    containerWidth: number,
    margin: [number, number],
    cols: number,
    containerPadding: [number, number]
  ) => {
    // Calculate column width
    // colWidth = (containerWidth - margins - padding) / cols
    const totalMargin = (cols - 1) * margin[0];
    const totalPadding = containerPadding ? containerPadding[0] * 2 : 0;
    const colWidth = (containerWidth - totalMargin - totalPadding) / cols;

    // Set row height equal to column width to make squares actually square
    setRowHeight(Math.max(50, colWidth));
    setCurrentCols(cols);
  };

  /* Menu code removed */

  return (
    <div
      className="w-full min-h-[500px] relative transition-all duration-300"
      style={{
        // Pass dynamic grid values as CSS variables for the background grid
        "--grid-margin": "16px",
        "--grid-row-height": `${rowHeight}px`,
        "--grid-cols-lg": "12",
        "--grid-cols-md": "10",
        "--grid-cols-sm": "6",
        "--grid-cols-xs": "4",
        "--grid-cols-xxs": "2",
      } as React.CSSProperties}
      onClick={(e) => {
        if (!isEditMode || !onAddCharts) return;
        const target = e.target as HTMLElement;
        if (target.dataset.gridCell === undefined && !target.classList.contains('react-grid-layout')) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        const totalMarginX = (currentCols - 1) * 16;
        const colWidth = (rect.width - totalMarginX) / currentCols;

        const x = Math.floor(clickX / (colWidth + 16));
        const y = Math.floor(clickY / (rowHeight + 16));

        onAddCharts({ x: Math.max(0, Math.min(x, currentCols - 1)), y: Math.max(0, y) });
      }}
      onContextMenu={(e) => handleContextMenu(e)}
    >
      {/* Background Grid - Only visible in Edit Mode */}
      {isEditMode && (
        <div
          className="absolute inset-0 z-0 grid transition-opacity duration-300 pointer-events-auto"
          style={{
            gridTemplateColumns: `repeat(${currentCols}, 1fr)`,
            gridAutoRows: `${rowHeight}px`,
            gap: '16px',
            opacity: 1,
          }}
        >
          {Array.from({ length: currentCols * totalRowsForBg }).map((_, i) => (
            <div
              key={i}
              data-grid-cell="true"
              className={`border border-accent-primary/10 bg-accent-primary/[0.01] rounded-xl ${isEditMode ? 'cursor-pointer hover:bg-accent-primary/[0.05]' : ''} `}
              style={{
                transition: "border-color 0.3s ease, background-color 0.3s ease",
              }}
            />
          ))}
        </div>
      )}

      <ResponsiveGridLayout
        className="layout relative z-10"
        layouts={layouts}
        breakpoints={{ xl: 1281, lg: 1025, md: 769, sm: 641, xs: 0 }}
        cols={{ xl: 24, lg: 24, md: 12, sm: 8, xs: 4 }}
        rowHeight={rowHeight}

        isDroppable={true}
        isDraggable={isEditMode}
        isResizable={isEditMode}
        onDrop={onDrop}
        onLayoutChange={(layout) => onLayoutChange(layout)}
        onDragStop={onDragStop}
        onResizeStop={onResizeStop}
        onDrag={onDrag}
        onResize={onResize}
        onWidthChange={handleWidthChange}
        droppingItem={droppingItem}
        margin={[16, 16]}
        containerPadding={[0, 0]}
        compactType="vertical"
        preventCollision={false}
        resizeHandles={isEditMode ? ["s", "w", "e", "n", "sw", "nw", "se", "ne"] : []}
        useCSSTransforms={true}
        style={{ minHeight: "500px" }}
      >
        {Array.isArray(items)
          ? items.map((item) => {
            const chartDef = getChartDefinition(item.type);
            const hasCustomHeader = chartDef?.hasCustomHeader || false;


            return (
              <div
                key={item.i}
                data-chart-id={item.i}
                className={isEditMode ? "" : "react-grid-item-locked cursor-default"}
                style={{ pointerEvents: "auto" }}
              >
                <ChartContainer
                  isEditMode={isEditMode}
                  isLoading={isChartLoading}
                  isError={isChartError}
                  chartName={item.type}
                  title={!hasCustomHeader ? (t(`charts.titles.${item.type}`) as string) : undefined}
                  description={!hasCustomHeader ? (t(`charts.descriptions.${item.type}`) as string) : undefined}

                  onClick={isChartError ? onChartErrorClick : undefined}
                  onRemove={
                    onRemoveItem ? () => onRemoveItem(item.i) : undefined
                  }
                >
                  {getChartComponent(item.type, { board, isEditMode })}
                </ChartContainer>
              </div>
            );
          })
          : null}
      </ResponsiveGridLayout>



      {/* Empty State - hide when dragging or in edit mode */}
      {items.length === 0 && !droppingItem && !isEditMode && (
        <div className="absolute inset-0 z-20 flex items-center justify-center p-6 text-center">
          <div className="flex flex-col items-center max-w-sm">
            <div className="w-16 h-16 bg-neutral-100 dark:bg-neutral-800 rounded-2xl flex items-center justify-center mb-6 text-neutral-400">
              <span className="w-8 h-8" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_boards_lamp.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_boards_lamp.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
            </div>
            <h3 className="text-base font-semibold text-neutral-700 dark:text-neutral-200 mb-2">
              {t("charts.addCharts")}
            </h3>
            <p className="text-sm text-neutral-400 dark:text-neutral-500 mb-6">
              {t("charts.dragInstructions")}
            </p>
            <button
              onClick={() => onAddCharts?.()}
              className="group flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-neutral-500 dark:text-neutral-400 hover:text-accent-primary dark:hover:text-accent-primary border border-dashed border-neutral-300 dark:border-neutral-700 hover:border-accent-primary/50 rounded-xl transition-all duration-200 hover:bg-accent-primary/5 dark:hover:bg-accent-primary/10 cursor-pointer pointer-events-auto"
            >
              <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_47ddeb53.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_47ddeb53.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
              {t("charts.addCharts")}
            </button>
          </div>
        </div>
      )}

      {/* Context Menu */}
      <BoardGridContextMenu
        isOpen={contextMenu.isOpen}
        x={contextMenu.x}
        y={contextMenu.y}
        targetChartId={contextMenu.targetChartId}
        onClose={() => setContextMenu(prev => ({ ...prev, isOpen: false }))}
        onDeleteChart={contextMenu.targetChartId ? () => onRemoveItem?.(contextMenu.targetChartId!) : undefined}
        onAddChart={() => {
          onToggleEditMode?.(true);
          onAddCharts?.();
        }}
        onRefresh={() => window.location.reload()}
      />
    </div>
  );
};
